/**
 * HOS v4.3 — Top-level execution shell.
 *
 * Routes by metadata.simulation_mode:
 *   - "form"               → 5-act narrative renderer
 *   - "browser_simulation" → IDE sandbox renderer
 *
 * State machine flow (form):
 *   hos_active_section: 0=intro, 1..5=acts, 6=submitted
 *
 * State machine flow (sandbox):
 *   hos_active_section: 0=intro, 1=active, 2=submitted
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle } from "lucide-react";
import { resolveBackendBase } from "../../lib/api";

import HOSIntroScreen from "./HOSIntroScreen";
import HOSFormRunner from "./HOSFormRunner";
import HOSSandboxRunner from "./HOSSandboxRunner";

const API_URL = `${resolveBackendBase()}/api/v2`;

export default function HOSExecutionPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [state, setState] = useState(null);
    const [beginning, setBeginning] = useState(false);
    const [beginError, setBeginError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(`${API_URL}/assessment/hos/${token}/state`);
            setState(r.data);
            return r.data;
        } catch (e) {
            setError(e.response?.data?.detail || "Failed to load assessment");
        }
    }, [token]);

    useEffect(() => {
        (async () => {
            await refresh();
            setLoading(false);
        })();
    }, [refresh]);

    const begin = async () => {
        setBeginning(true);
        setBeginError(null);
        try {
            await axios.post(`${API_URL}/assessment/hos/${token}/begin`);
            await refresh();
        } catch (e) {
            setBeginError(e.response?.data?.detail || "Could not start the simulation.");
        }
        setBeginning(false);
    };

    if (loading) return <Spinner />;
    if (error) return <ErrorPanel msg={error} />;

    const mode = state?.mode || "form";
    const section = state?.hos_active_section ?? 0;
    const completed = state?.status === "completed" || (mode === "form" ? section === 6 : section === 2);

    if (completed) return <HOSSubmittedScreen state={state} />;
    if (section === 0) return <HOSIntroScreen data={state} onBegin={begin} beginning={beginning} error={beginError} />;
    if (mode === "form") return <HOSFormRunner state={state} onAdvance={refresh} />;
    return <HOSSandboxRunner state={state} onAdvance={refresh} />;
}


function Spinner() {
    return (
        <div className="min-h-screen bg-background flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
        </div>
    );
}

function ErrorPanel({ msg }) {
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 p-8 max-w-md text-center" data-testid="hos-error-screen">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
                <h1 className="text-lg font-display font-bold text-white mb-2">Unable to load simulation</h1>
                <p className="text-sm text-gray-500">{msg}</p>
            </div>
        </div>
    );
}

function HOSSubmittedScreen({ state }) {
    const meta = state?.template?.content?.metadata || {};
    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
            <div className="max-w-xl w-full" data-testid="hos-submitted-screen">
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-emerald-300 mb-3">Simulation submitted</p>
                <h1 className="text-3xl font-display font-bold text-white mb-3 leading-tight">Thank you. We've got everything.</h1>
                <p className="text-sm text-gray-400 leading-relaxed mb-8">
                    Your responses, file edits, console history, and test runs have been captured. THCO will follow up.
                </p>
                <p className="text-[11px] text-gray-600 font-mono">Assessment ID: <span className="text-gray-400">{meta.assessment_id || "—"}</span></p>
            </div>
        </div>
    );
}
